import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Users, Building2, Clock, ShieldCheck, Wallet, TrendingUp, ArrowRight } from "lucide-react";

function KpiCard({ icon: Icon, label, value, tint, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-100 bg-white p-5 hover:shadow-lg hover:shadow-slate-200/60 transition-all">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${tint}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-2xl font-display font-extrabold text-slate-900 font-mono">{value}</div>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-1">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [trends, setTrends] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/payroll").then((r) => setRecent(r.data.slice(0, 5))).catch(() => {});
    api.get("/stats/trends").then((r) => setTrends(r.data)).catch(() => {});
  }, []);

  return (
    <div className="animate-fade-up">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-sky-600">Dashboard</p>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 mt-1">
            Welcome back, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-slate-500 mt-1">Here's what's happening across your payroll operations.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {user?.role === "admin" && (
          <KpiCard testid="kpi-companies" icon={Building2} label="Companies" value={stats?.companies ?? "—"} tint="bg-sky-50 text-sky-600" />
        )}
        <KpiCard testid="kpi-employees" icon={Users} label="Employees" value={stats?.employees ?? "—"} tint="bg-indigo-50 text-indigo-600" />
        <KpiCard testid="kpi-active-shifts" icon={Clock} label="Active Shifts" value={stats?.active_shifts ?? "—"} tint="bg-emerald-50 text-emerald-600" />
        <KpiCard testid="kpi-pending" icon={ShieldCheck} label="Pending Approvals" value={stats?.pending_approvals ?? "—"} tint="bg-amber-50 text-amber-600" />
        <KpiCard testid="kpi-disbursed" icon={TrendingUp} label="Total Disbursed" value={stats ? money(stats.total_disbursed) : "—"} tint="bg-sky-50 text-sky-600" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 rounded-2xl border border-slate-100 bg-white p-6" data-testid="wages-trend-chart">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg">Wage trends</h2>
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Recent weeks</span>
          </div>
          {trends.length === 0 ? (
            <div className="h-[240px] flex items-center justify-center text-slate-400 text-sm">No payroll data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={trends} margin={{ left: -10, right: 8, top: 8 }}>
                <defs>
                  <linearGradient id="gGross" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0284c7" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#0284c7" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gNet" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#38bdf8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="week" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => `$${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`} />
                <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
                <Area type="monotone" dataKey="gross" name="Gross" stroke="#0284c7" strokeWidth={2.5} fill="url(#gGross)" dot={{ r: 3, fill: "#0284c7" }} />
                <Area type="monotone" dataKey="net" name="Net" stroke="#38bdf8" strokeWidth={2.5} fill="url(#gNet)" dot={{ r: 3, fill: "#38bdf8" }} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="rounded-2xl border border-slate-100 bg-white p-6" data-testid="hours-trend-chart">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg">Hours logged</h2>
          </div>
          {trends.length === 0 ? (
            <div className="h-[240px] flex items-center justify-center text-slate-400 text-sm">No hours yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={trends} margin={{ left: -18, right: 8, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="week" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => `${v} h`} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
                <Bar dataKey="hours" name="Hours" fill="#0284c7" radius={[6, 6, 0, 0]} maxBarSize={38} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-slate-100 bg-white">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <h2 className="font-display font-bold text-lg">Recent Payroll Runs</h2>
            <Link to="/app/payroll" className="text-sm font-semibold text-sky-600 inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="view-all-payroll">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {recent.length === 0 && <p className="px-6 py-10 text-center text-slate-400 text-sm">No payroll runs yet.</p>}
            {recent.map((p) => (
              <Link key={p.id} to={`/app/payroll/${p.id}`} data-testid={`recent-payroll-${p.id}`}
                className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors">
                <div>
                  <p className="font-semibold text-slate-800">{p.company_name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">Week of {new Date(p.week_start).toLocaleDateString()} · {p.totals.employees} employees</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-mono font-bold text-sky-600">{money(p.totals.net)}</span>
                  <StatusBadge status={p.status} />
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-slate-900 text-white p-6 flex flex-col justify-between">
          <div>
            <Wallet className="w-8 h-8 text-sky-400 mb-4" />
            <h2 className="font-display font-bold text-xl">Quick actions</h2>
            <p className="text-slate-400 text-sm mt-1">Move payroll forward.</p>
          </div>
          <div className="space-y-2 mt-6">
            <Link to="/app/employees" data-testid="quick-add-employee" className="block rounded-xl bg-slate-800 hover:bg-slate-700 px-4 py-3 text-sm font-semibold transition-colors">+ Add an employee</Link>
            <Link to="/app/time" data-testid="quick-track-time" className="block rounded-xl bg-slate-800 hover:bg-slate-700 px-4 py-3 text-sm font-semibold transition-colors">⏱ Track time</Link>
            <Link to="/app/payroll" data-testid="quick-run-payroll" className="block rounded-xl bg-sky-500 hover:bg-sky-600 px-4 py-3 text-sm font-semibold transition-colors">▸ Run weekly payroll</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
